import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Target, GraduationCap, ShoppingBag, CalendarDays, MessageCircle, Smartphone, Handshake } from "lucide-react";
import { heroStats, programs, stayInGame, media } from "../mock";

const iconFor = {
  "Adult Coaching": Target,
  "Youth Darts Academy": GraduationCap,
  "Pro Shop": ShoppingBag,
};
const stayIcon = {
  "Tournament Calendars": CalendarDays,
  "Community Chat": MessageCircle,
  "Bulls on Parade Darts": Smartphone,
};

export default function Home() {
  return (
    <main className="bop-vignette">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:px-8 py-16 lg:grid-cols-2 lg:py-24">
          <div className="bop-fade-up">
            <p className="eyebrow">Ontario’s Darts Hub</p>
            <h1 className="mt-5 font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Where Ontario <span className="text-[#ff2e4d]">aims true.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-400">
              Coaching, a youth darts academy, pro-shop supplies, and the sponsors keeping the local
              scene thrown — all under one roof, built for throwers across Ontario.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/programs#coaching"
                className="inline-flex items-center gap-2 rounded-lg bg-[#ff2e4d] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(255,46,77,0.8)] hover:bg-[#e01f3c] transition-colors"
              >
                Book a Coaching Session <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/programs#supplies"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Browse the Pro Shop
              </Link>
            </div>
          </div>

          <div className="bop-fade-up relative" style={{ animationDelay: "0.12s" }}>
            <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
              <img
                src={media.hero}
                alt="A bristle dartboard lit by a single spotlight in a dark charcoal room, darts clustered in the bullseye"
                className="h-[380px] w-full object-cover sm:h-[460px] lg:h-[520px]"
                loading="eager"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="border-y border-white/10 bg-[#0f0f11]">
          <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-white/10 px-4 sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:px-6 lg:px-8">
            {heroStats.map((s) => (
              <div key={s.big} className="px-2 py-8 text-center">
                <div className="font-display text-3xl font-extrabold text-[#ff2e4d]">{s.big}</div>
                <div className="mt-1 text-sm text-zinc-400">{s.small}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE RUN */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <p className="eyebrow">The Programs</p>
          <h2 className="mt-3 font-display text-4xl font-bold text-white">What we run</h2>
          <p className="mt-4 text-zinc-400">
            Three programs, one hub. Pick a lane below or see everything on the Programs & Pro Shop page.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {programs.map((p) => {
            const Icon = iconFor[p.title] || Target;
            return (
              <Link key={p.title} to={p.to} className="bop-card group flex flex-col rounded-2xl p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ff2e4d]/15 text-[#ff2e4d]">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold text-white">{p.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-400">{p.body}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#ff2e4d]">
                  {p.cta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* STAY IN THE GAME */}
      <section className="border-y border-white/10 bg-[#0f0f11]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <p className="eyebrow">Bookmarks</p>
            <h2 className="mt-3 font-display text-4xl font-bold text-white">Stay in the game</h2>
            <p className="mt-4 text-zinc-400">A few places every Ontario thrower should have bookmarked.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {stayInGame.map((s) => {
              const Icon = stayIcon[s.title] || CalendarDays;
              const inner = (
                <>
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-zinc-500 transition-colors group-hover:text-[#ff2e4d]" />
                  </div>
                  <h3 className="mt-6 font-display text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{s.body}</p>
                </>
              );
              return s.external ? (
                <a key={s.title} href={s.to} target="_blank" rel="noopener noreferrer" className="bop-card group rounded-2xl p-7">
                  {inner}
                </a>
              ) : (
                <Link key={s.title} to={s.to} className="bop-card group rounded-2xl p-7">
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SPONSORS CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={media.sponsorsVenue} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c0c0e] via-[#0c0c0e]/85 to-[#0c0c0e]/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#ff2e4d]/40 bg-[#ff2e4d]/10 px-3 py-1 text-xs font-semibold text-[#ff2e4d]">
              <Handshake className="h-4 w-4" /> Sponsorship
            </span>
            <h2 className="mt-5 font-display text-4xl font-bold text-white sm:text-5xl">Powering Ontario darts</h2>
            <p className="mt-4 text-lg text-zinc-300">
              Bulls On Parade runs on the businesses that back the local scene. See sponsorship tiers
              and how to get involved.
            </p>
            <Link
              to="/programs#sponsors"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#ff2e4d] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors"
            >
              See Sponsorship <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
